import React, { useState, useMemo } from 'react';
import { Database, Layers, Tag, Search } from 'lucide-react';
import { getHighlightedSearchText } from '../utils/helpers';

const setHasId = (set, id) => {
    if (id == null) return false;
    const key = String(id);
    for (const value of set) {
        if (String(value) === key) return true;
    }
    return false;
};

const DataAssetTreeView = ({
    assets = [],
    selectedDatasource = '',
    selectedDatasets = new Set(),
    selectedAttributes = new Set(),
    onSelectionChange
}) => {
    // Search filter state
    const [searchQuery, setSearchQuery] = useState('');

    const renderHighlighted = (text) => getHighlightedSearchText(text, searchQuery);

    // Keep track of manually collapsed nodes (default is expanded)
    const [collapsedNodes, setCollapsedNodes] = useState(new Set());

    // Group assets into hierarchy: Datasource -> Dataset -> Attributes
    const hierarchy = useMemo(() => {
        const dsMap = new Map();

        assets.forEach(asset => {
            const dsName = asset.datasource || 'Default Datasource';
            const dtName = asset.dataset || 'Default Dataset';

            if (!dsMap.has(dsName)) {
                dsMap.set(dsName, new Map());
            }
            const dtMap = dsMap.get(dsName);
            if (!dtMap.has(dtName)) {
                dtMap.set(dtName, []);
            }
            dtMap.get(dtName).push(asset);
        });

        const result = [];
        dsMap.forEach((dtMap, dsName) => {
            const datasets = [];
            dtMap.forEach((attrList, dtName) => {
                datasets.push({
                    name: dtName,
                    attributes: attrList
                });
            });
            result.push({
                name: dsName,
                datasets
            });
        });

        return result;
    }, [assets]);

    // Filter hierarchy by search query
    const filteredHierarchy = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return hierarchy;

        return hierarchy.map(ds => {
            const dsMatches = ds.name.toLowerCase().includes(q);
            const filteredDatasets = ds.datasets.map(dt => {
                const dtMatches = dt.name.toLowerCase().includes(q);
                const filteredAttributes = dt.attributes.filter(attr => {
                    const attrClean = String(attr.attribute || '').split('.').pop() || attr.attribute;
                    return attr.attribute.toLowerCase().includes(q) || attrClean.toLowerCase().includes(q);
                });

                if (dsMatches || dtMatches || filteredAttributes.length > 0) {
                    return {
                        ...dt,
                        attributes: (dsMatches || dtMatches) ? dt.attributes : filteredAttributes
                    };
                }
                return null;
            }).filter(Boolean);

            if (dsMatches || filteredDatasets.length > 0) {
                return {
                    ...ds,
                    datasets: dsMatches ? ds.datasets : filteredDatasets
                };
            }
            return null;
        }).filter(Boolean);
    }, [hierarchy, searchQuery]);

    const isNodeExpanded = (nodeId) => searchQuery.trim() ? true : !collapsedNodes.has(nodeId);

    const toggleNodeExpand = (nodeId, e) => {
        if (e) e.stopPropagation();
        setCollapsedNodes(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    // Helper functions to check selection states
    const getDatasetAttrIds = (dt) => dt.attributes.map(a => a.id);
    const getDatasourceAttrIds = (ds) => ds.datasets.flatMap(dt => dt.attributes.map(a => a.id));
    const getDatasourceDatasetNames = (ds) => ds.datasets.map(dt => dt.name);

    // Toggle entire Datasource
    const handleDatasourceToggle = (ds, checked) => {
        const nextDatasets = new Set(selectedDatasets);
        const nextAttributes = new Set(selectedAttributes);
        const dsAttrs = getDatasourceAttrIds(ds);
        const dsDatasets = getDatasourceDatasetNames(ds);

        if (checked) {
            dsDatasets.forEach(d => nextDatasets.add(d));
            dsAttrs.forEach(id => nextAttributes.add(id));
        } else {
            dsDatasets.forEach(d => nextDatasets.delete(d));
            dsAttrs.forEach(id => nextAttributes.delete(id));
        }

        const nextDs = checked ? ds.name : (nextAttributes.size > 0 ? selectedDatasource : '');
        onSelectionChange({
            datasource: nextDs,
            selectedDatasets: nextDatasets,
            selectedAttributes: nextAttributes
        });
    };

    // Toggle entire Dataset
    const handleDatasetToggle = (dsName, dt, checked) => {
        const nextDatasets = new Set(selectedDatasets);
        const nextAttributes = new Set(selectedAttributes);
        const dtAttrs = getDatasetAttrIds(dt);

        if (checked) {
            nextDatasets.add(dt.name);
            dtAttrs.forEach(id => nextAttributes.add(id));
        } else {
            nextDatasets.delete(dt.name);
            dtAttrs.forEach(id => nextAttributes.delete(id));
        }

        onSelectionChange({
            datasource: dsName,
            selectedDatasets: nextDatasets,
            selectedAttributes: nextAttributes
        });
    };

    // Toggle single Attribute
    const handleAttributeToggle = (dsName, dtName, assetId, checked) => {
        const nextDatasets = new Set(selectedDatasets);
        const nextAttributes = new Set(selectedAttributes);

        if (checked) {
            nextAttributes.add(assetId);
            nextDatasets.add(dtName);
        } else {
            nextAttributes.delete(assetId);
            const dsObj = hierarchy.find(d => d.name === dsName);
            const dtObj = dsObj ? dsObj.datasets.find(t => t.name === dtName) : null;
            if (dtObj) {
                const hasRemaining = dtObj.attributes.some(a => setHasId(nextAttributes, a.id));
                if (!hasRemaining) {
                    nextDatasets.delete(dtName);
                }
            }
        }

        onSelectionChange({
            datasource: dsName,
            selectedDatasets: nextDatasets,
            selectedAttributes: nextAttributes
        });
    };

    return (
        <div className="tw-space-y-3">
            {/* Tree View Search Bar
            <div className="tw-relative tw-w-full">
                <span className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-flex tw-items-center">
                    <Search size={14} />
                </span>
                <input
                    type="text"
                    className="form-control !tw-py-1.5 !tw-pl-8 !tw-pr-8 !tw-text-xs !tw-h-[34px] tw-rounded-lg tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-bg-white dark:tw-bg-[#1E1E2D] dark:tw-text-white"
                    placeholder="Search data assets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                    <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 dark:hover:tw-text-white tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center"
                    >
                        <X size={13} />
                    </button>
                )}
            </div> */}

            {/* Tree View Content Container */}
            <div className="modal-tree-view tw-border tw-border-solid tw-border-[#E2E8F0] dark:tw-border-white/10 tw-rounded-xl tw-bg-white dark:tw-bg-[#1E1E2D] tw-pb-4 tw-max-h-[380px] tw-overflow-y-auto">
                {/* Tree View Search Bar */}
                <div className="tw-w-full tw-p-4 tw-bg-white tw-sticky tw-top-0 tw-z-10">
                    <div className={`tw-relative tw-w-full tw-bg-white ${searchQuery && searchQuery.trim() !== "" ? "has-btn-close" : ""}`}>
                        <span className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-flex tw-items-center">
                            <Search size={14} />
                        </span>
                        <input
                            type="text"
                            className="form-control !tw-py-1.5 !tw-pl-8 !tw-pr-8 !tw-text-xs !tw-h-[34px] tw-rounded-lg tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-bg-white dark:tw-bg-[#1E1E2D] dark:tw-text-white"
                            placeholder="Search data assets..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && searchQuery.trim() !== "" && (
                            <button
                                type="button"
                                className="btn btn-clear !tw-right-2.5"
                                title="Clear"
                                onClick={() => setSearchQuery('')}
                            >
                                <i className="icon-close"></i>
                            </button>
                        )}
                    </div>
                </div>
                {filteredHierarchy.length > 0 ? (
                    <div className='tw-px-4'>
                    <ul className="qd_tree-view vertical-lined-tree-view">
                        {filteredHierarchy.map(ds => {
                    const dsNodeId = `ds_${ds.name}`;
                    const isDsExpanded = isNodeExpanded(dsNodeId);
                    const dsAttrs = getDatasourceAttrIds(ds);
                    const checkedDsAttrs = dsAttrs.filter(id => setHasId(selectedAttributes, id));
                    const isDsAllChecked = dsAttrs.length > 0 && checkedDsAttrs.length === dsAttrs.length;
                    const isDsIndeterminate = checkedDsAttrs.length > 0 && checkedDsAttrs.length < dsAttrs.length;

                    return (
                        <li key={ds.name}>
                            <div
                                id={`Datasource-left-panel-div-${ds.name}`}
                                className={`tw-flex tw-flex-nowrap tw-items-center tw-gap-1.5 tw-py-1 ${isDsExpanded ? "caret caret-down" : "caret"}`}
                                onClick={(e) => toggleNodeExpand(dsNodeId, e)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isDsAllChecked}
                                    ref={el => { if (el) el.indeterminate = isDsIndeterminate; }}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleDatasourceToggle(ds, e.target.checked);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer tw-shrink-0"
                                />

                                <Database size={15} className="tw-text-[#475569] dark:tw-text-[#94A3B8] tw-shrink-0 tw-ml-0.5" />

                                <span className="tw-text-xs tw-font-semibold tw-text-[#1E293B] dark:tw-text-white tw-truncate" title={ds.name}>
                                    {renderHighlighted(ds.name)}
                                </span>

                                {/* Selected children count badges */}
                                {(() => {
                                    const checkedDatasetCount = ds.datasets.filter(dt =>
                                        getDatasetAttrIds(dt).some(id => setHasId(selectedAttributes, id))
                                    ).length;
                                    const checkedAttrCount = checkedDsAttrs.length;
                                    if (checkedDatasetCount === 0 && checkedAttrCount === 0) return null;
                                    return (
                                        <div className="tree-nodes-enabled-count tw-ml-1 tw-flex tw-items-center tw-gap-1 tw-shrink-0" style={{ whiteSpace: 'nowrap' }}>
                                            {checkedDatasetCount > 0 && (
                                                <span className="!tw-inline-flex tw-items-center tw-text-[11px]" style={{ whiteSpace: 'nowrap' }}>[<span className="text-blue tw-inline-flex tw-items-center tw-gap-0.5"><Layers size={11} className="tw-shrink-0" />&nbsp;{checkedDatasetCount}</span>]</span>
                                            )}
                                            {checkedAttrCount > 0 && (
                                                <span className="!tw-inline-flex tw-items-center tw-text-[11px]" style={{ whiteSpace: 'nowrap' }}>[<span className="text-blue tw-inline-flex tw-items-center tw-gap-0.5"><Tag size={11} className="tw-shrink-0" />&nbsp;{checkedAttrCount}</span>]</span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Level 2: Datasets */}
                            <ul className={isDsExpanded ? "nested active" : "nested"}>
                                {ds.datasets.map(dt => {
                                    const dtNodeId = `dt_${ds.name}_${dt.name}`;
                                    const isDtExpanded = isNodeExpanded(dtNodeId);
                                    const dtAttrs = getDatasetAttrIds(dt);
                                    const checkedDtAttrs = dtAttrs.filter(id => setHasId(selectedAttributes, id));
                                    const isDtAllChecked = dtAttrs.length > 0 && checkedDtAttrs.length === dtAttrs.length;
                                    const isDtIndeterminate = checkedDtAttrs.length > 0 && checkedDtAttrs.length < dtAttrs.length;

                                    return (
                                        <li key={dt.name}>
                                            <div
                                                id={`Dataset-left-panel-div-${dt.name}`}
                                                className={`tw-flex tw-flex-nowrap tw-items-center tw-gap-1.5 tw-py-1 ${isDtExpanded ? "caret caret-down" : "caret"}`}
                                                onClick={(e) => toggleNodeExpand(dtNodeId, e)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isDtAllChecked}
                                                    ref={el => { if (el) el.indeterminate = isDtIndeterminate; }}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleDatasetToggle(ds.name, dt, e.target.checked);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer tw-shrink-0"
                                                />

                                                <Layers size={14} className="tw-text-[#475569] dark:tw-text-[#94A3B8] tw-shrink-0 tw-ml-0.5" />

                                                <span className="tw-text-xs tw-font-medium tw-text-[#334155] dark:tw-text-gray-200 tw-truncate" title={dt.name}>
                                                    {renderHighlighted(dt.name)}
                                                </span>

                                                {/* Selected attributes count badge */}
                                                {checkedDtAttrs.length > 0 && (
                                                    <div className="tree-nodes-enabled-count tw-ml-1 tw-flex tw-items-center tw-shrink-0" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className="!tw-inline-flex tw-items-center tw-text-[11px]" style={{ whiteSpace: 'nowrap' }}>[<span className="text-blue tw-inline-flex tw-items-center tw-gap-0.5"><Tag size={11} className="tw-shrink-0" />&nbsp;{checkedDtAttrs.length}</span>]</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Level 3 & 4: Select All Attributes (with chevron caret) & Attributes list */}
                                            {isDtExpanded && (
                                                <ul className={isDtExpanded ? "nested active" : "nested"}>
                                                    {(() => {
                                                        const selectAllNodeId = `selectAll_${ds.name}_${dt.name}`;
                                                        const isSelAllExpanded = isNodeExpanded(selectAllNodeId);

                                                        return (
                                                            <li>
                                                                <div
                                                                    id={`SelectAll-left-panel-div-${dt.name}`}
                                                                    className={`tw-flex tw-flex-nowrap tw-items-center tw-gap-1.5 tw-py-1 ${isSelAllExpanded ? "caret caret-down" : "caret"}`}
                                                                    onClick={(e) => toggleNodeExpand(selectAllNodeId, e)}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isDtAllChecked}
                                                                        ref={el => { if (el) el.indeterminate = isDtIndeterminate; }}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDatasetToggle(ds.name, dt, e.target.checked);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer tw-shrink-0"
                                                                    />

                                                                    <span className="tw-text-xs tw-font-medium tw-text-[#475569] dark:tw-text-gray-300">
                                                                        Select all Attributes
                                                                    </span>

                                                                    {/* Selected attributes count badge */}
                                                                    {checkedDtAttrs.length > 0 && (
                                                                        <div className="tree-nodes-enabled-count tw-ml-1 tw-flex tw-items-center tw-shrink-0" style={{ whiteSpace: 'nowrap' }}>
                                                                            <span className="!tw-inline-flex tw-items-center tw-text-[11px]" style={{ whiteSpace: 'nowrap' }}>[<span className="text-blue tw-inline-flex tw-items-center tw-gap-0.5"><Tag size={11} className="tw-shrink-0" />&nbsp;{checkedDtAttrs.length}</span>]</span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <ul className={isSelAllExpanded ? "nested active has-no-caret" : "nested has-no-caret"}>
                                                                    {dt.attributes.map(attr => {
                                                                        const isAttrChecked = setHasId(selectedAttributes, attr.id);
                                                                        const attrClean = String(attr.attribute || '').split('.').pop() || attr.attribute;

                                                                        return (
                                                                            <li key={attr.id}>
                                                                                <label className="tw-flex tw-items-center tw-gap-2 tw-py-1 tw-cursor-pointer tw-mb-0" onClick={(e) => e.stopPropagation()}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isAttrChecked}
                                                                                        onChange={(e) => handleAttributeToggle(ds.name, dt.name, attr.id, e.target.checked)}
                                                                                        className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer tw-shrink-0"
                                                                                    />

                                                                                    <Tag size={13} className="tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0" />

                                                                                    <span className="tw-text-xs tw-text-[#475569] dark:tw-text-gray-200 tw-font-normal tw-truncate" title={attrClean}>
                                                                                        {renderHighlighted(attrClean)}
                                                                                    </span>
                                                                                </label>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </li>
                                                        );
                                                    })()}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    );
                })}
            </ul></div>
                ) : (
                    <div className="tw-text-center tw-py-6 tw-text-xs tw-text-gray-400">
                        No matching data assets found
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataAssetTreeView;
